"use client";

import type { ChatStructuredAsset, ChatStructuredPriceChange } from "../types";

import NextImage from "../../../NextImage";
import React from "react";
import { buildJsonRenderAssetDetailPath } from "@components/Chat/JsonRenderComponents/buildJsonRenderAssetDetailPath";
import { cn } from "utils";
import { useShellRouter } from "@hooks/useShellAwareRouter";

export interface ChatAssetInlineProps {
  asset: ChatStructuredAsset;
  /** 头像尺寸：lg 对应 32px，md 对应 24px，sm 对应 20px */
  size?: "sm" | "md" | "lg";
  /** 可选价格行（Ed's Picks） */
  price?: ChatStructuredPriceChange;
  /** 名称是否与 symbol 同一行展示（Alpha 部分稿） */
  nameInline?: boolean;
  /** 表格等场景使用较小主标题 */
  compactSymbol?: boolean;
  /** 覆盖 symbol 文本 class */
  symbolClassName?: string;
  className?: string;

  hideName?: boolean;
}

const sizeMap = {
  sm: { wh: 20 },
  md: { wh: 24 },
  lg: { wh: 32 },
} as const;

/**
 * 横向资产行：头像 + 简称 + 可选副标题/价格。
 */
export const ChatAssetInline: React.FC<ChatAssetInlineProps> = ({
  asset,
  size = "md",
  nameInline = false,
  compactSymbol = false,
  symbolClassName,
  className,
  hideName = false,
}) => {
  const router = useShellRouter();
  const dim = sizeMap[size];
  const schemaDetailPath =
    asset.schemaAssetType && asset.symbol
      ? buildJsonRenderAssetDetailPath(
          asset.schemaAssetType,
          asset.symbol,
          asset.id,
        )
      : null;

  const handleAssetIconClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (asset.url) {
      event.preventDefault();
      event.stopPropagation();
      router.push(asset.url);
      return;
    }
    if (schemaDetailPath) {
      event.preventDefault();
      event.stopPropagation();
      router.push(schemaDetailPath);
    }
  };

  const isAvatarClickable = Boolean(asset.url || schemaDetailPath);
  return (
    <div
      className={cn(
        "flex min-w-0 flex-1 items-center",
        compactSymbol ? "gap-2" : "gap-1",
        className,
      )}
    >
      <div
        className={cn(
          "relative shrink-0 overflow-hidden border border-border_solid rounded-full",
          isAvatarClickable && "cursor-pointer",
        )}
        style={{ width: dim.wh, height: dim.wh }}
        data-openreplay-label="chatbox_chatassetinline_click"
        onClick={handleAssetIconClick}
      >
        <NextImage
          src={asset.imageUrl}
          alt={asset.symbol}
          width={dim.wh}
          height={dim.wh}
          className="size-full object-cover"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        {nameInline ? (
          <div className="flex min-w-0 items-center gap-1 whitespace-nowrap">
            <span
              className={cn(
                compactSymbol ? "body_medium_s" : "body_bold_m",
                "min-w-0 truncate text-content_primary",
                symbolClassName,
              )}
            >
              {asset.symbol}
            </span>
            {asset.name ? (
              <span className="body_s min-w-0 truncate text-content_quaternary">
                {asset.name}
              </span>
            ) : null}
          </div>
        ) : (
          <>
            <div className="flex min-w-0 items-center gap-1 whitespace-nowrap">
              <span
                className={cn(
                  "min-w-0 truncate text-content_primary",
                  compactSymbol
                    ? "body_medium_s"
                    : size === "lg"
                      ? "body_bold_m"
                      : "title_l",
                  symbolClassName,
                )}
              >
                {asset.symbol}
              </span>
            </div>
            {!hideName && asset.name ? (
              <span
                className={cn(
                  "min-w-0 truncate",
                  compactSymbol
                    ? "body_s text-content_secondary"
                    : "body_s text-content_quaternary",
                )}
              >
                {asset.name}
              </span>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};
